import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { platformWorkAPI, taxonomyAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { BookOpen, Search, Copy, CheckCircle2, ExternalLink, Sparkles, Loader2, FileText, Tag, X, Save } from 'lucide-react';
import { toast } from 'sonner';

export const KnowledgeBasePage = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editTags, setEditTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);

  const catLabel = (v) => categoryOptions.find(o => o.value === v)?.label || v;
  const allTags = [...new Set(articles.flatMap(a => a.kb_tags || []))].sort();

  const openArticle = (a) => { setActive(a); setEditCategory(a.kb_category || ''); setEditTags(a.kb_tags || []); setTagInput(''); };
  const addTag = () => { const t = tagInput.trim(); if (t && !editTags.includes(t)) setEditTags([...editTags, t]); setTagInput(''); };
  const saveMeta = async () => {
    if (!active) return;
    setSavingMeta(true);
    try {
      await platformWorkAPI.update(active.task_id, { kb_category: editCategory || null, kb_tags: editTags });
      toast.success('Category & tags saved');
      setActive(a => ({ ...a, kb_category: editCategory || null, kb_tags: editTags }));
      load();
    } catch { toast.error('Failed to save'); } finally { setSavingMeta(false); }
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setArticles(await platformWorkAPI.getKbArticles());
    } catch { toast.error('Failed to load KB articles'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    taxonomyAPI.getAll().then(cats => {
      const c = cats.find(x => x.id === 'kb_category');
      setCategoryOptions((c?.items || []).filter(i => i.active));
    }).catch(() => {});
  }, []);

  const filtered = articles.filter(a => {
    if (categoryFilter && a.kb_category !== categoryFilter) return false;
    if (tagFilter && !(a.kb_tags || []).includes(tagFilter)) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return `${a.title} ${a.project_title || ''} ${a.platform || ''} ${(a.kb_tags || []).join(' ')} ${a.kb_article || ''}`.toLowerCase().includes(s);
  });

  const copyArticle = (a) => {
    const tmp = document.createElement('div'); tmp.innerHTML = a.kb_article || '';
    navigator.clipboard.writeText(tmp.innerText || '');
    toast.success('Article copied — ready to paste into LevelUp');
  };

  const togglePublish = async (a) => {
    try {
      await platformWorkAPI.update(a.task_id, { kb_published: !a.kb_published });
      toast.success(a.kb_published ? 'Marked as unpublished' : 'Marked as pushed to LevelUp');
      setActive(prev => prev ? { ...prev, kb_published: !a.kb_published } : prev);
      load();
    } catch { toast.error('Failed to update'); }
  };

  const publishedCount = articles.filter(a => a.kb_published).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="kb-library-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold flex items-center gap-2"><BookOpen className="w-7 h-7 text-lime" />Knowledge Base Library</h1>
          <p className="text-muted-foreground">AI-generated articles from completed LevelUp++ tasks, ready to push to LevelUp</p>
        </div>
        <div className="flex gap-3 text-sm">
          <div className="text-center px-4 py-2 rounded-lg bg-muted"><div className="text-xl font-bold">{articles.length}</div><div className="text-xs text-muted-foreground">Articles</div></div>
          <div className="text-center px-4 py-2 rounded-lg bg-green-50"><div className="text-xl font-bold text-green-700">{publishedCount}</div><div className="text-xs text-muted-foreground">Pushed</div></div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search articles by title, project, platform, tag or content..." className="pl-9" data-testid="kb-search-input" />
        </div>
        <Select value={categoryFilter || 'all'} onValueChange={v => setCategoryFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[180px]" data-testid="kb-category-filter"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categoryOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {allTags.length > 0 && (
          <Select value={tagFilter || 'all'} onValueChange={v => setTagFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[160px]" data-testid="kb-tag-filter"><SelectValue placeholder="All Tags" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tags</SelectItem>
              {allTags.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="h-40 bg-muted animate-pulse rounded-lg" />)}</div>
      ) : filtered.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(a => (
            <Card key={a.task_id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => openArticle(a)} data-testid={`kb-card-${a.task_id}`}>
              <CardContent className="pt-5 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-lime flex-shrink-0" />
                    <h3 className="font-medium text-sm truncate">{a.title}</h3>
                  </div>
                  {a.kb_published && <Badge className="bg-green-100 text-green-800 flex-shrink-0"><CheckCircle2 className="w-3 h-3 mr-1" />Pushed</Badge>}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {a.kb_category && <Badge className="bg-lime/20 text-lime-foreground text-xs">{catLabel(a.kb_category)}</Badge>}
                  {a.platform && <Badge variant="outline" className="text-xs">{a.platform}</Badge>}
                  {a.project_title && <Badge variant="secondary" className="text-xs">{a.project_title}</Badge>}
                </div>
                {(a.kb_tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 items-center">
                    <Tag className="w-3 h-3 text-muted-foreground" />
                    {a.kb_tags.map(t => <span key={t} className="text-[11px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{t}</span>)}
                  </div>
                )}
                <p className="text-xs text-muted-foreground line-clamp-2">{(a.kb_article || '').replace(/<[^>]+>/g, ' ').slice(0, 140)}…</p>
                <p className="text-[11px] text-muted-foreground">{a.kb_generated_at ? `Generated ${a.kb_generated_at.slice(0,10)}` : ''}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No knowledge base articles yet</h3>
          <p className="text-muted-foreground mb-4">Open a completed task and click "Generate KB Article" to build one.</p>
          <Button onClick={() => navigate('/platform')} className="bg-lime text-lime-foreground hover:bg-lime/90">Go to Platform Tracker</Button>
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="kb-article-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">{active?.title}{active?.kb_published && <Badge className="bg-green-100 text-green-800">Pushed to LevelUp</Badge>}</DialogTitle>
            <DialogDescription>
              {active?.platform && `${active.platform} · `}{active?.project_title || 'Standalone task'}{active?.kb_generated_at ? ` · Generated ${active.kb_generated_at.slice(0,10)}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 border-b pb-3 mb-2">
            <Button size="sm" variant="outline" onClick={() => copyArticle(active)} data-testid="kb-dialog-copy"><Copy className="w-4 h-4 mr-1.5" />Copy for LevelUp</Button>
            <Button size="sm" onClick={() => togglePublish(active)} data-testid="kb-dialog-publish" className={active?.kb_published ? '' : 'bg-lime text-lime-foreground hover:bg-lime/90'} variant={active?.kb_published ? 'outline' : 'default'}>
              <ExternalLink className="w-4 h-4 mr-1.5" />{active?.kb_published ? 'Mark as Not Pushed' : 'Mark as Pushed to LevelUp'}
            </Button>
            {active && <Button size="sm" variant="ghost" onClick={() => navigate(`/platform/tasks/${active.task_id}`)} data-testid="kb-dialog-open-task">Open Task</Button>}
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-4" data-testid="kb-meta-editor">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Category</label>
              <Select value={editCategory || 'none'} onValueChange={v => setEditCategory(v === 'none' ? '' : v)}>
                <SelectTrigger className="mt-1" data-testid="kb-edit-category"><SelectValue placeholder="Uncategorized" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Uncategorized —</SelectItem>
                  {categoryOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Tags</label>
              <div className="flex flex-wrap gap-1 mt-1 mb-1.5">
                {editTags.map(t => (
                  <span key={t} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-muted" data-testid={`kb-tag-chip-${t}`}>
                    {t}<button type="button" onClick={() => setEditTags(editTags.filter(x => x !== t))}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} placeholder="Add tag + Enter" className="h-8 text-sm" data-testid="kb-tag-input" />
                <Button size="sm" variant="outline" onClick={addTag} data-testid="kb-tag-add">Add</Button>
              </div>
            </div>
            <div className="sm:col-span-2">
              <Button size="sm" onClick={saveMeta} disabled={savingMeta} data-testid="kb-save-meta" className="bg-lime text-lime-foreground hover:bg-lime/90">
                {savingMeta ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}Save Category &amp; Tags
              </Button>
            </div>
          </div>

          <div className="prose prose-sm max-w-none" data-testid="kb-dialog-content" dangerouslySetInnerHTML={{ __html: active?.kb_article || '' }} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KnowledgeBasePage;
